<script setup lang="ts">
    import { inject, ref, watch } from 'vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { type Event, CustomSenderSource } from '@views/Event/EventTypes';
    import { eventKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;

    const customSenderName = ref(CustomSenderSource.Off);
    const customReplyTo = ref(CustomSenderSource.Off);

    watch(
        () => event.value?.CustomSenderName,
        (value) => (customSenderName.value = value ?? CustomSenderSource.Off),
        { immediate: true }
    );
    watch(
        () => event.value?.CustomReplyTo,
        (value) => (customReplyTo.value = value ?? CustomSenderSource.Off),
        { immediate: true }
    );

    const senderNameOptions = [
        { label: $Resources.EventCustomSenderNameOff, value: CustomSenderSource.Off },
        { label: $Resources.EventCustomSenderNameOrganizer, value: CustomSenderSource.Organizer },
        { label: $Resources.EventCustomSenderNameContactPerson, value: CustomSenderSource.ContactPerson },
    ];
    const replyToOptions = [
        { label: $Resources.EventCustomReplyToOff, value: CustomSenderSource.Off },
        { label: $Resources.EventCustomReplyToOrganizer, value: CustomSenderSource.Organizer },
        { label: $Resources.EventCustomReplyToContactPerson, value: CustomSenderSource.ContactPerson },
    ];

    function onCustomSenderNameChange(value: CustomSenderSource) {
        customSenderName.value = value;
        saveEventProperty('CustomSenderName', String(value), $Resources.EventCustomSenderNameHeading);
    }

    function onCustomReplyToChange(value: CustomSenderSource) {
        customReplyTo.value = value;
        saveEventProperty('CustomReplyTo', String(value), $Resources.EventCustomReplyToHeading);
    }
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t m-lg-b">{{ $Resources.EventCommunicationSettings }}</h2>

        <FeatureLock feature="Event.CustomSender" showUpgradeAlert :infoTitle="$Resources.EventCustomSenderFeatureHeading">
            <div class="event-card">
                <h3 class="event-card-heading">{{ $Resources.EventCustomSenderNameHeading }}</h3>

                <div class="m-xs-t">
                    <div
                        class="form-group"
                        :class="senderNameOptions.length !== index + 1 && 'm-sm-b'"
                        v-for="(option, index) in senderNameOptions"
                        :key="option.value">
                        <FormCheckable
                            :key="option.value"
                            type="radio"
                            name="customSenderName"
                            :label="option.label"
                            :switchStyle="SwitchStyle.Standard"
                            :checked="customSenderName === option.value"
                            :disabled="!canEdit"
                            @update:modelValue="onCustomSenderNameChange(option.value)" />
                    </div>
                </div>

                <BsAlert
                    v-if="customSenderName !== CustomSenderSource.Off"
                    :type="AlertTypes.Info"
                    :dismissible="false"
                    :content="$Resources.EventCustomSenderNameFallbackInfo" />
            </div>

            <div class="event-card">
                <h3 class="event-card-heading">{{ $Resources.EventCustomReplyToHeading }}</h3>

                <div class="m-xs-t">
                    <div
                        class="form-group"
                        :class="replyToOptions.length !== index + 1 && 'm-sm-b'"
                        v-for="(option, index) in replyToOptions"
                        :key="option.value">
                        <FormCheckable
                            :key="option.value"
                            type="radio"
                            name="customReplyTo"
                            :label="option.label"
                            :switchStyle="SwitchStyle.Standard"
                            :checked="customReplyTo === option.value"
                            :disabled="!canEdit"
                            @update:modelValue="onCustomReplyToChange(option.value)" />
                    </div>
                </div>

                <BsAlert
                    v-if="customReplyTo !== CustomSenderSource.Off"
                    :type="AlertTypes.Info"
                    :dismissible="false"
                    :content="$Resources.EventCustomReplyToFallbackInfo" />
            </div>
        </FeatureLock>
    </div>
</template>
